import { ApiError } from '../../services/api';
import type {
  AdminOfferUpdateRequest,
  OfferDiscountType,
  OfferPreviewRequest,
  OfferResponse,
  OfferStatus,
} from '../../types/offers';
import { STATUS_LABELS } from './offerReviewModel';

/**
 * #997 — admins edit a garage offer. Form model, diff, validation and error mapping for
 * `OfferEditPage`. Mirrors the partner portal's `components/offers/offerUtils.ts` limits
 * (the portals share no code); the server stays authoritative.
 */

export const TITLE_MAX = 60;
export const SUBTITLE_MAX = 90;
export const MAX_WINDOW_DAYS = 90;
export const PERCENT_MIN = 5;
export const PERCENT_MAX = 70;
export const FIXED_MIN = 5;
export const MAX_PER_BOOKING_MIN = 5;
export const MAX_BOOKINGS_MAX = 10000;

/** A running offer keeps its start date (server: OFFER_WINDOW_INVALID). */
export const START_LOCKED_STATUSES: readonly OfferStatus[] = ['LIVE', 'PAUSED'];

export const PENDING_REVISION_HINT = "Approve or reject the garage's pending change first";
export const STATE_CHANGED_EDIT_MESSAGE = 'This offer changed, reload and try again';

export interface EditGate {
  /** Show the Edit action at all (hidden for ENDED, SUPERSEDED and revision rows). */
  visible: boolean;
  /** Edit is allowed now. */
  allowed: boolean;
  /** Why not, when visible but not allowed. */
  hint: string | null;
}

/** Which offers an admin may edit: not ENDED/SUPERSEDED, not a revision row, no pending partner revision. */
export function editGate(offer: Pick<OfferResponse, 'status' | 'revisionOf' | 'pendingRevision'>): EditGate {
  if (offer.revisionOf || offer.status === 'ENDED' || offer.status === 'SUPERSEDED') {
    return { visible: false, allowed: false, hint: null };
  }
  if (offer.pendingRevision?.status === 'PENDING_APPROVAL') {
    return { visible: true, allowed: false, hint: PENDING_REVISION_HINT };
  }
  return { visible: true, allowed: true, hint: null };
}

// ── Form model ──
/** Spec 073: one covered service and its own discount value (as typed). */
export interface OfferEditItem {
  offeringId: string;
  discountValue: string;
}

export interface OfferEditForm {
  titleEn: string;
  titleAr: string;
  subtitleEn: string;
  subtitleAr: string;
  startsAt: string;
  endsAt: string;
  discountType: OfferDiscountType;
  /** Spec 073: UI-only "Default discount" (never sent): pre-fills newly checked services, copied by "Apply to all". */
  defaultDiscount: string;
  maxDiscountPerBooking: string;
  maxBookings: string;
  /** Covered services, each with its own value. */
  items: OfferEditItem[];
  bannerStorageKey: string | null;
  bannerUrl: string | null;
  bannerArStorageKey: string | null;
  bannerArUrl: string | null;
}

/** Spec 073: a per-service value error is keyed `item:<offeringId>` and shown under that row. */
export type ItemField = `item:${string}`;
export const itemField = (offeringId: string): ItemField => `item:${offeringId}`;

export type EditField =
  | 'titleEn' | 'titleAr' | 'subtitleEn' | 'subtitleAr'
  | 'startsAt' | 'endsAt'
  | 'discountValue' | 'maxDiscountPerBooking' | 'maxBookings' | 'offeringIds'
  | 'banner' | 'bannerAr'
  | ItemField;

export type EditFieldErrors = Partial<Record<EditField, string>>;

const numStr = (n: number | null | undefined) => (n == null ? '' : String(n));

export function formFromOffer(o: OfferResponse): OfferEditForm {
  // Spec 073: a row without its own value stays blank (no fallback to the offer value); submit flags it.
  const items = (o.items ?? []).map((i) => ({ offeringId: i.offeringId, value: i.discountValue ?? null }));
  const values = items.map((i) => i.value).filter((v): v is number => v != null && Number.isFinite(v));
  return {
    titleEn: o.titleEn ?? '',
    titleAr: o.titleAr ?? '',
    subtitleEn: o.subtitleEn ?? '',
    subtitleAr: o.subtitleAr ?? '',
    startsAt: (o.startsAt ?? '').slice(0, 10),
    endsAt: (o.endsAt ?? '').slice(0, 10),
    discountType: o.discountType ?? 'PERCENT',
    // The default starts from the highest service value (design §2).
    defaultDiscount: numStr(values.length ? Math.max(...values) : o.discountValue),
    maxDiscountPerBooking: numStr(o.maxDiscountPerBooking),
    maxBookings: numStr(o.maxBookings),
    items: items.map((i) => ({ offeringId: i.offeringId, discountValue: numStr(i.value) })),
    bannerStorageKey: o.banner?.storageKey ?? null,
    bannerUrl: o.banner?.downloadUrl ?? null,
    bannerArStorageKey: o.bannerAr?.storageKey ?? null,
    bannerArUrl: o.bannerAr?.downloadUrl ?? null,
  };
}

const optText = (s: string) => (s.trim() ? s.trim() : null);
const optNum = (s: string) => (s.trim() === '' ? null : Number(s));

/** Spec 073: the typed per-service values as numbers (null when empty or not a number). */
export function itemValues(f: Pick<OfferEditForm, 'items'>): (number | null)[] {
  return f.items.map((i) => {
    const n = optNum(i.discountValue);
    return n != null && Number.isFinite(n) ? n : null;
  });
}

/** Lowest typed per-service value (the offer column the server keeps); null when none. */
export function minItemValue(f: Pick<OfferEditForm, 'items'>): number | null {
  const v = itemValues(f).filter((n): n is number => n != null);
  return v.length ? Math.min(...v) : null;
}

/** "Apply to all": copies the default discount to every covered service. */
export function applyDefaultToAll(f: OfferEditForm): OfferEditForm {
  return { ...f, items: f.items.map((i) => ({ ...i, discountValue: f.defaultDiscount })) };
}

/** Checks or unchecks a service; a newly checked one takes the default discount. */
export function toggleItem(f: OfferEditForm, offeringId: string): OfferEditForm {
  const on = f.items.some((i) => i.offeringId === offeringId);
  return {
    ...f,
    items: on
      ? f.items.filter((i) => i.offeringId !== offeringId)
      : [...f.items, { offeringId, discountValue: f.defaultDiscount }],
  };
}

export function setItemValue(f: OfferEditForm, offeringId: string, value: string): OfferEditForm {
  return { ...f, items: f.items.map((i) => (i.offeringId === offeringId ? { ...i, discountValue: value } : i)) };
}

export function formToRequest(f: OfferEditForm, version: number): AdminOfferUpdateRequest {
  const values = itemValues(f);
  return {
    titleEn: f.titleEn.trim(),
    titleAr: optText(f.titleAr),
    subtitleEn: optText(f.subtitleEn),
    subtitleAr: optText(f.subtitleAr),
    startsAt: f.startsAt,
    endsAt: f.endsAt,
    discountType: f.discountType,
    // The server recomputes it as MIN(items).
    discountValue: minItemValue(f) ?? optNum(f.defaultDiscount),
    maxDiscountPerBooking: f.discountType === 'PERCENT' ? optNum(f.maxDiscountPerBooking) : null,
    maxBookings: optNum(f.maxBookings),
    offeringIds: f.items.map((i) => i.offeringId),
    items: f.items.map((i, idx) => ({ offeringId: i.offeringId, discountValue: values[idx] })),
    bannerStorageKey: f.bannerStorageKey,
    bannerArStorageKey: f.bannerArStorageKey,
    version,
  };
}

/**
 * The draft rule for the per-variant preview; null while it cannot be priced yet. Spec 073: each
 * service priced with its own value (`items`); `discountValue` is the lowest, like the save request.
 */
export function draftRule(f: OfferEditForm): OfferPreviewRequest | null {
  const values = itemValues(f).map((v) => (v != null && v > 0 ? v : null));
  const value = minItemValue({ items: f.items.filter((_, i) => values[i] != null) });
  if (value == null || f.items.length === 0) return null;
  const cap = f.discountType === 'PERCENT' ? optNum(f.maxDiscountPerBooking) : null;
  return {
    discountType: f.discountType,
    discountValue: value,
    maxDiscountPerBooking: cap != null && Number.isFinite(cap) && cap > 0 ? cap : null,
    offeringIds: f.items.map((i) => i.offeringId),
    items: f.items.map((i, idx) => ({ offeringId: i.offeringId, discountValue: values[idx] })),
  };
}

// ── Diff (confirm step) ──
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

/**
 * Labels of the fields that differ from the loaded offer, in form order. Empty = nothing to save.
 * Spec 073: a changed service value is named per service ("Discount (Oil change)").
 */
export function changedFields(original: OfferEditForm, f: OfferEditForm, nameOf: (offeringId: string) => string = (id) => id): string[] {
  const out: string[] = [];
  const text = (a: string, b: string) => a.trim() !== b.trim();
  if (text(original.titleEn, f.titleEn)) out.push('Title (English)');
  if (text(original.titleAr, f.titleAr)) out.push('Title (Arabic)');
  if (text(original.subtitleEn, f.subtitleEn)) out.push('Subtitle (English)');
  if (text(original.subtitleAr, f.subtitleAr)) out.push('Subtitle (Arabic)');
  if (original.startsAt !== f.startsAt) out.push('Start date');
  if (original.endsAt !== f.endsAt) out.push('End date');
  const cap = (x: OfferEditForm) => (x.discountType === 'PERCENT' ? optNum(x.maxDiscountPerBooking) : null);
  if (original.discountType !== f.discountType || cap(original) !== cap(f)) out.push('Discount');
  const before = new Map(original.items.map((i) => [i.offeringId, optNum(i.discountValue)]));
  for (const item of f.items) {
    if (before.has(item.offeringId) && before.get(item.offeringId) !== optNum(item.discountValue)) {
      out.push(`Discount (${nameOf(item.offeringId)})`);
    }
  }
  if (optNum(original.maxBookings) !== optNum(f.maxBookings)) out.push('Booking limit');
  if (!sameSet(original.items.map((i) => i.offeringId), f.items.map((i) => i.offeringId))) out.push('Services');
  if (original.bannerStorageKey !== f.bannerStorageKey) out.push('Banner (English)');
  if (original.bannerArStorageKey !== f.bannerArStorageKey) out.push('Banner (Arabic)');
  return out;
}

export function confirmSummary(changes: string[], status: OfferStatus): string {
  return `You're changing: ${changes.join(', ')}. The garage will be notified. The offer stays ${STATUS_LABELS[status].toLowerCase()}.`;
}

// ── Validation (early hints; the server is authoritative) ──
function daysBetween(fromIso: string, toIso: string): number {
  const ms = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return Date.UTC(y, (m || 1) - 1, d || 1);
  };
  return Math.round((ms(toIso) - ms(fromIso)) / 86400000);
}

export function validateEdit(
  f: OfferEditForm,
  ctx: {
    originalStartsAt: string;
    today: string;
    bookingsUsed: number;
    /** Spec 073: a service's "from" list price when known (FIXED must stay AED 1 below it). */
    priceOf?: (offeringId: string) => number | null;
  },
): EditFieldErrors {
  const e: EditFieldErrors = {};
  if (!f.titleEn.trim()) e.titleEn = 'Enter an English title.';
  else if (f.titleEn.trim().length > TITLE_MAX) e.titleEn = `At most ${TITLE_MAX} characters.`;
  if (f.titleAr.trim().length > TITLE_MAX) e.titleAr = `At most ${TITLE_MAX} characters.`;
  if (f.subtitleEn.trim().length > SUBTITLE_MAX) e.subtitleEn = `At most ${SUBTITLE_MAX} characters.`;
  if (f.subtitleAr.trim().length > SUBTITLE_MAX) e.subtitleAr = `At most ${SUBTITLE_MAX} characters.`;
  if (!f.startsAt) e.startsAt = 'Pick a start date.';
  else if (f.startsAt !== ctx.originalStartsAt && f.startsAt < ctx.today) e.startsAt = 'The offer cannot start before today.';
  if (!f.endsAt) e.endsAt = 'Pick an end date.';
  // Reviewer #1007: never an end before today (the backend 422s it). For a running offer a past end
  // would drop it from the catalog and the sweeper would END it for good.
  else if (f.endsAt < ctx.today) e.endsAt = 'The end date must be today or later.';
  else if (f.startsAt && f.endsAt <= f.startsAt) e.endsAt = 'The end date must be after the start date.';
  else if (f.startsAt && daysBetween(f.startsAt, f.endsAt) > MAX_WINDOW_DAYS) e.endsAt = `An offer can run for up to ${MAX_WINDOW_DAYS} days.`;
  if (f.items.length === 0) e.offeringIds = 'Pick at least one service.';
  // Spec 073: every covered service needs its own value in range.
  for (const item of f.items) {
    const raw = item.discountValue.trim();
    const v = Number(raw);
    const key = itemField(item.offeringId);
    if (f.discountType === 'PERCENT') {
      if (raw === '' || !Number.isFinite(v) || v < PERCENT_MIN || v > PERCENT_MAX) e[key] = `Enter a percentage from ${PERCENT_MIN} to ${PERCENT_MAX}.`;
    } else if (raw === '' || !Number.isFinite(v) || v < FIXED_MIN) {
      e[key] = `Enter at least AED ${FIXED_MIN}.`;
    } else {
      const price = ctx.priceOf?.(item.offeringId) ?? null;
      if (price != null && v > price - 1) e[key] = `Must be at least AED 1 below this service's price (AED ${price}).`;
    }
  }
  if (f.discountType === 'PERCENT' && f.maxDiscountPerBooking.trim() !== '') {
    const m = Number(f.maxDiscountPerBooking);
    if (!Number.isFinite(m) || m < MAX_PER_BOOKING_MIN) e.maxDiscountPerBooking = `At least AED ${MAX_PER_BOOKING_MIN}.`;
  }
  if (f.maxBookings.trim() !== '') {
    const mb = Number(f.maxBookings);
    if (!Number.isInteger(mb) || mb < 1 || mb > MAX_BOOKINGS_MAX) e.maxBookings = `Enter a whole number from 1 to ${MAX_BOOKINGS_MAX}, or leave empty.`;
    else if (mb < ctx.bookingsUsed) e.maxBookings = `Cannot be below the ${ctx.bookingsUsed} bookings already used.`;
  }
  if (!f.bannerStorageKey) e.banner = 'Add an English banner.';
  return e;
}

// ── API errors ──
const SERVER_FIELD: Record<string, EditField> = {
  titleEn: 'titleEn', titleAr: 'titleAr', subtitleEn: 'subtitleEn', subtitleAr: 'subtitleAr',
  startsAt: 'startsAt', endsAt: 'endsAt',
  discountType: 'discountValue', discountValue: 'discountValue',
  maxDiscountPerBooking: 'maxDiscountPerBooking', maxBookings: 'maxBookings',
  offeringIds: 'offeringIds', bannerStorageKey: 'banner', bannerArStorageKey: 'bannerAr',
};

/** Fallback text per code, used when the server sends no message. */
const CODE_TEXT: Record<string, string> = {
  DISCOUNT_OUT_OF_RANGE: `The discount is out of range (${PERCENT_MIN}–${PERCENT_MAX}% or at least AED ${FIXED_MIN}).`,
  DISCOUNT_EXCEEDS_PRICE: 'The discount is larger than a covered service price.',
  OFFER_WINDOW_INVALID: 'The offer dates are not valid.',
  NO_ACTIVE_SERVICE: 'Pick at least one active service.',
  BANNER_REQUIRED: 'Add an English banner.',
  ARABIC_REQUIRED: 'The Arabic title is required.',
  MAX_BOOKINGS_BELOW_USED: 'The booking limit is below the bookings already used.',
  CONTACT_DETAILS_FORBIDDEN: 'Contact details and links are not allowed in offers.',
  INVALID_STORAGE_KEY: 'The uploaded banner could not be verified. Upload it again.',
};

export interface EditErrorView {
  fieldErrors: EditFieldErrors;
  formError: string | null;
  /** Show a Reload button (stale version). */
  reload: boolean;
  /** Editing is blocked until the partner's revision is decided. */
  blocked: boolean;
}

/** Spec 073: `items[2].discountValue` → the request's item index (2). */
const ITEM_FIELD_RE = /^items\[(\d+)]\.discountValue$/;

/** Spec 073: `itemIds` = the request's item order, so `items[<i>]` errors land under that service row. */
export function mapEditError(err: unknown, itemIds: string[] = []): EditErrorView {
  const out: EditErrorView = { fieldErrors: {}, formError: null, reload: false, blocked: false };
  if (!(err instanceof ApiError)) {
    out.formError = err instanceof Error && err.message ? err.message : 'Could not save the offer.';
    return out;
  }
  const serverMessage = err.serverMessage ?? err.message;
  switch (err.code) {
    case 'REVISION_PENDING':
      return { ...out, formError: PENDING_REVISION_HINT, blocked: true };
    case 'OFFER_STATE_CHANGED':
      return { ...out, formError: STATE_CHANGED_EDIT_MESSAGE, reload: true };
    case 'OFFER_ENDED':
      return { ...out, formError: 'This offer has ended and can no longer be edited.', blocked: true };
    case 'FEATURE_DISABLED':
      return { ...out, formError: 'Offers are temporarily disabled' };
    default:
      break;
  }
  if (err.status === 503) return { ...out, formError: 'Offers are temporarily disabled' };
  if (err.status === 403) return { ...out, formError: 'You do not have access to edit offers.' };
  if (err.status === 404) return { ...out, formError: 'This offer no longer exists.' };

  const entries = Object.entries(err.errors ?? {});
  for (const [serverField, value] of entries) {
    const itemMatch = ITEM_FIELD_RE.exec(serverField);
    const itemId = itemMatch ? itemIds[Number(itemMatch[1])] : undefined;
    const field: EditField | undefined = itemMatch ? (itemId ? itemField(itemId) : 'offeringIds') : SERVER_FIELD[serverField];
    if (!field) continue;
    // One field: the server message speaks about it. Several: say what each field's code means.
    const text = entries.length === 1 ? serverMessage : (CODE_TEXT[value] ?? serverMessage);
    out.fieldErrors[field] = text || CODE_TEXT[value] || value;
  }
  if (Object.keys(out.fieldErrors).length === 0) {
    out.formError = serverMessage || (err.code && CODE_TEXT[err.code]) || 'Could not save the offer.';
  } else {
    out.formError = 'Fix the highlighted fields.';
  }
  return out;
}

// ── Dates ──
export function todayInDubai(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** ISO instant → "13 Oct 2026, 01:30" in Asia/Dubai. */
export function formatDubaiInstant(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    timeZone: 'Asia/Dubai', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** The earliest end date the picker offers: the later of the start date and today (Asia/Dubai). */
export function minEndDate(startsAt: string, today: string): string {
  return startsAt && startsAt > today ? startsAt : today;
}
